import { useEffect, useState } from 'react';
import { Title, useCreatePath } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import {
  FEATURE_INQUIRIES,
  INQUIRY_STATUSES,
  fetchInquiries,
  moneyText,
  toErrorMessage,
  type InquiryFilter,
  type InquiryRow,
} from './inquiriesApi';

/** Kolor stanu — zamknięte szare, przyjęte zielone, czekające na decyzję pomarańczowe. */
export function statusColor(status: string): 'success' | 'warning' | 'default' | 'info' {
  if (status === 'accepted') return 'success';
  if (status === 'priced' || status === 'offer_sent') return 'info';
  if (status === 'to_check') return 'warning';
  return 'default';
}

const QUICK_FILTERS: Array<{ key: keyof InquiryFilter; label: string; testId: string }> = [
  { key: 'open', label: 'Otwarte', testId: 'inquiry-filter-open' },
  { key: 'awaitingApproval', label: 'Czekają na zatwierdzenie ceny', testId: 'inquiry-filter-approval' },
  { key: 'forwarded', label: 'Przekazane do FTL', testId: 'inquiry-filter-forwarded' },
  { key: 'tradeSignal', label: 'Sygnał do Trade', testId: 'inquiry-filter-trade' },
];

/**
 * Zapytania klienta (26.10.2026, P3; przełącznik `clientInquiries`,
 * S-26.10-08). Lista zapytań ze stanem, wskazanym autem, przekazaniem do
 * FTL, sygnałem do Trade i ostatnią wyceną. Zakłada CS; FTL i Trade
 * oglądają (filtry „Przekazane do FTL”, „Czekają na zatwierdzenie ceny”).
 */
export const InquiryList = () => {
  const on = useFeatureState(FEATURE_INQUIRIES);
  const canCreate = useHasPermission('ORDER_INTAKE');
  const createPath = useCreatePath();
  const [filter, setFilter] = useState<InquiryFilter>({ open: '1' });
  const [rows, setRows] = useState<InquiryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (on !== true) return;
    let alive = true;
    const t = setTimeout(() => {
      fetchInquiries(filter)
        .then((r) => {
          if (!alive) return;
          setRows(r);
          setError(null);
        })
        .catch((e) => alive && setError(toErrorMessage(e)));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [on, filter]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  const toggle = (key: keyof InquiryFilter) =>
    setFilter((f) => {
      const next = { ...f };
      if (next[key]) delete next[key];
      else next[key] = '1';
      return next;
    });

  return (
    <Box sx={{ p: 1 }} data-testid="inquiry-list">
      <Title title="Zapytania" />
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 320px' }}>
              Zapytania klientów o dostępność auta i cenę. Zapytanie to ten sam rekord co prezlecenie — po przyjęciu
              oferty staje się prezleceniem. Do klienta nic nie wychodzi z systemu.
            </Typography>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={Link}
                to={createPath({ resource: 'inquiries', type: 'create' })}
                data-testid="inquiry-create"
              >
                Nowe zapytanie
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {QUICK_FILTERS.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                color={filter[f.key] ? 'primary' : 'default'}
                variant={filter[f.key] ? 'filled' : 'outlined'}
                onClick={() => toggle(f.key)}
                data-testid={f.testId}
              />
            ))}
            <TextField
              select
              size="small"
              label="Stan"
              value={filter.status ?? ''}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value || undefined }))}
              sx={{ minWidth: 180 }}
              slotProps={{ htmlInput: { 'data-testid': 'inquiry-filter-status' } }}
            >
              <MenuItem value="">wszystkie</MenuItem>
              {INQUIRY_STATUSES.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Klient albo miejscowość"
              value={filter.q ?? ''}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value || undefined }))}
            />
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {rows && rows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Brak zapytań dla tych filtrów.
            </Typography>
          )}
          {rows && rows.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zapytanie</TableCell>
                    <TableCell>Klient</TableCell>
                    <TableCell>Relacja</TableCell>
                    <TableCell>Załadunek</TableCell>
                    <TableCell>Stan</TableCell>
                    <TableCell>Wycena</TableCell>
                    <TableCell>Uwagi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover data-testid="inquiry-row">
                      <TableCell>
                        <Link to={createPath({ resource: 'inquiries', type: 'show', id: r.id })}>{r.ref}</Link>
                      </TableCell>
                      <TableCell>{r.clientName ?? '—'}</TableCell>
                      <TableCell>
                        {r.loading ?? '—'} → {r.unloading ?? '—'}
                      </TableCell>
                      <TableCell>{r.loadingDate ? dateTimeNoSeconds(r.loadingDate) : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.statusLabel} color={statusColor(r.status)} />
                        {r.statusReason && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {r.statusReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.quote ? (
                          <>
                            {moneyText(r.quote.price, r.quote.currency)}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {r.quote.offerSent
                                ? 'oferta wysłana'
                                : r.quote.approved
                                  ? 'zatwierdzona'
                                  : 'czeka na zatwierdzenie'}
                            </Typography>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {r.indicatedCarName && <Chip size="small" variant="outlined" label={`wskazane: ${r.indicatedCarName}`} />}
                          {r.forwardedToFtlAt && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={r.ftlResponse ? `FTL: ${r.ftlResponse === 'possible' ? 'możliwe' : 'niemożliwe'}` : 'u FTL'}
                            />
                          )}
                          {r.tradeSignalAt && <Chip size="small" variant="outlined" label="sygnał do Trade" />}
                          {r.kind === null && <Chip size="small" color="success" variant="outlined" label="jest prezleceniem" />}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
